import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons"
import { Flex, Progress, Tag, Tooltip, Typography } from "antd"
import { type ColumnsType } from "antd/es/table"
import React from "react"
import type { TFunction } from "react-i18next"

import { empty } from "../../../../../../../constants/placeholders"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { parseAmount } from "../../../../../../../utils/number/parse-amount.ts"
import type { ProjectSubcontractor } from "../../../../../types/project-subcontractors.types.tsx"
import {
  MILESTONE_PAYMENT_LABEL_KEYS,
  MILESTONE_PAYMENT_TAG_COLORS,
  getMilestonePaymentStatus
} from "./milestone-payment-status.ts"

const { Text, Link } = Typography

interface GetColumnsArgs {
  t: TFunction
  onItemSelected: (record: ProjectSubcontractor) => void
}

const RECEIVABLE = 1

const TYPE_TAG_COLORS: Record<string, string> = {
  Subcontractor: "green",
  Supplier: "purple",
  Customer: "blue",
  "Team Member": "geekblue",
  Miscellaneous: "default",
  Reimbursement: "gold",
  Expense: "volcano",
  Material: "cyan"
}

// Derived from the same status the milestone tag uses, so the two can never
// contradict each other by construction rather than by coincidence. Rounding is
// the trap: 40/10000 rounds to 0 and 9999/10000 rounds to 100, either of which
// would put "partially settled" beside a bar reading nothing or everything.
const percentComplete = (total: string, completed: string): number => {
  const status = getMilestonePaymentStatus(total, completed)
  if (status === "none" || status === "pending") return 0

  const totalNum = parseAmount(total)
  // No planned value to divide by; the tag already calls it settled.
  if (totalNum <= 0) return 100

  const exact = (parseAmount(completed) / totalNum) * 100
  // Overpaid rows keep their true figure so the bar still flags them red.
  if (status === "completed") return Math.round(exact)
  return Math.min(99, Math.max(1, Math.round(exact)))
}

export const getColumns = ({
  t,
  onItemSelected
}: GetColumnsArgs): ColumnsType<ProjectSubcontractor> => [
  {
    title: "Name",
    dataIndex: "full_name",
    key: "full_name",
    width: 180,
    ellipsis: true,
    render: (full_name: string, record) => {
      const name = full_name || empty
      return record?.filter_params ? (
        <Tooltip title="Filter the transactions above by this contact">
          <Link
            className="cursor-pointer"
            onClick={() => onItemSelected(record)}
          >
            {name}
          </Link>
        </Tooltip>
      ) : (
        <Text className="text-inherit" ellipsis={{ tooltip: name }}>
          {name}
        </Text>
      )
    }
  },
  {
    title: "Source/Target",
    dataIndex: "type_display",
    key: "type_display",
    width: 130,
    render: (type_display: string) =>
      type_display ? (
        <Tag color={TYPE_TAG_COLORS[type_display] ?? "default"} className="m-0">
          {type_display}
        </Tag>
      ) : (
        empty
      )
  },
  {
    title: "Type",
    dataIndex: "financial_type",
    key: "financial_type",
    width: 130,
    render: (financial_type: number, record) => {
      const isReceivable = financial_type === RECEIVABLE
      const isExpense = record.is_expense
      const colorClass = isExpense
        ? "text-orange-600"
        : isReceivable
          ? "text-green-600"
          : "text-blue-600"
      return (
        <Flex align="center" gap={6} className={colorClass}>
          {isReceivable ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          <Text className="text-inherit">{record.financial_type_display}</Text>
        </Flex>
      )
    }
  },
  {
    title: "Contract Total",
    dataIndex: "total",
    key: "total",
    width: 140,
    sorter: true,
    render: (total: string) => (
      <Text className="text-inherit font-medium">{budgetFormat(total)}</Text>
    )
  },
  {
    title: "Completed",
    dataIndex: "completed",
    key: "completed",
    width: 140,
    sorter: true,
    render: (completed: string) => (
      <Text className="text-green-600 font-medium">
        {budgetFormat(completed)}
      </Text>
    )
  },
  {
    title: "Pending",
    dataIndex: "pending",
    key: "pending",
    width: 140,
    sorter: true,
    render: (pending: string) => (
      <Text className="text-orange-600 font-medium">
        {budgetFormat(pending)}
      </Text>
    )
  },
  {
    title: "% Completed",
    dataIndex: "completed",
    key: "percent_completed",
    width: 160,
    render: (_value, record) => {
      const percent = percentComplete(record.total, record.completed)
      const overpaid = percent > 100
      return (
        <Flex align="center" gap={8}>
          <Progress
            percent={percent}
            showInfo={false}
            size="small"
            status={overpaid ? "exception" : undefined}
            className="min-w-16 flex-1"
          />
          <Text
            type={overpaid ? "danger" : undefined}
            className={overpaid ? "text-xs" : "text-inherit text-xs"}
          >
            {percent}%
          </Text>
        </Flex>
      )
    }
  },
  {
    title: "Linked Milestones",
    dataIndex: "milestones",
    key: "milestones",
    width: 220,
    render: (milestones: ProjectSubcontractor["milestones"], record) => {
      const status = getMilestonePaymentStatus(record.total, record.completed)

      return milestones?.length > 0 ? (
        <Flex gap={8} wrap="wrap">
          {milestones.map((milestone) => (
            <Tooltip
              key={milestone.id}
              title={`${milestone.name} — ${t(MILESTONE_PAYMENT_LABEL_KEYS[status])}`}
            >
              <Tag
                color={MILESTONE_PAYMENT_TAG_COLORS[status]}
                className="m-0 truncate"
              >
                {milestone.name}
              </Tag>
            </Tooltip>
          ))}
        </Flex>
      ) : (
        empty
      )
    }
  }
]
